import { useCallback, useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Snowflake, Lock, Plus, SkipForward, GraduationCap } from 'lucide-react';
import { MarathonPhasesPreview } from '../../screens/MarathonPhasesPreview';
import { marathonApi, type MarathonAccountDetail, type MarathonAccount, type MarathonBoard, type MarathonCohort } from '../../lib/api';
import { CohortEmblem } from './CohortEmblems';
import { INK, PANEL, RIM, ICE, TEAL, WIN, LOSS, TITLE_FONT, PX_FONT, pixelFrame, ACCENT, FIELD, GOLD, NUM_FONT, PANEL_GLASS, CASE, ON_ACCENT, FIELD_SHADOW, HEAD_SHADOW } from '../guild/theme';

const label: CSSProperties = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.16em', textTransform: CASE, color: TEAL };
const input: CSSProperties = {
  width: '100%', background: FIELD, color: ICE, border: 'none', padding: '8px 9px',
  fontFamily: PX_FONT, fontSize: 12, boxShadow: FIELD_SHADOW, outline: 'none',
};
const usd = (n: number) => `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString()}`;
const k = (n: number) => `$${Math.round(n / 1000)}k`;
const COHORT_NAME: Record<MarathonCohort, string> = { M: 'Master Cohort', A: 'Cohort A', B: 'Cohort B', C: 'Cohort C', D: 'Cohort D' };

function Panel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <section style={{ background: PANEL_GLASS, padding: 18, boxShadow: pixelFrame(), ...style }}>{children}</section>;
}

function Title({ children }: { children: ReactNode }) {
  return <div style={{ marginBottom: 10 }}><span className="pal-chapter" style={{ fontFamily: TITLE_FONT, fontSize: 20, color: ICE, textShadow: HEAD_SHADOW }}>{children}</span></div>;
}

function Btn({ children, onClick, tone = TEAL, solid = false, disabled = false }: { children: ReactNode; onClick: () => void; tone?: string; solid?: boolean; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{
        fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.1em', textTransform: CASE, padding: '6px 9px', border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
        background: solid ? tone : FIELD, color: solid ? INK : tone, boxShadow: `0 0 0 2px ${solid ? INK : tone}`,
      }}>
      {children}
    </button>
  );
}

function Kpi({ name, value, sub, tone }: { name: string; value: string; sub?: string; tone?: string }) {
  return (
    <div style={{ background: PANEL, padding: '12px 14px', boxShadow: `0 0 0 2px ${RIM}, 0 0 0 4px ${INK}` }}>
      <div style={label}>{name}</div>
      <div style={{ fontFamily: NUM_FONT, fontSize: 28, lineHeight: 1.1, color: tone ?? ICE, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ ...label, fontSize: 9, color: 'var(--pal-axis)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

type NodeState = 'on' | 'off' | 'iced' | 'retired';

/* Where an account stands: the start tick sits at a fixed point, the bar's left end is the max loss and its
   right end the goal (eval target, or a funded account's payout line). Below start it fills red toward the
   left, reaching the end at the loss line; above, green toward the right, reaching the end at the goal. */
const START_AT = 30; // percent along the bar
function StandBar({ a, height = 6 }: { a: MarathonAccount; height?: number }) {
  const goal = a.status === 'eval' ? a.target : a.payoutLine;
  const up = a.balance >= a.size;
  const lossSpan = a.size > a.floor ? a.size - a.floor : a.mdd;
  const gainSpan = goal != null && goal > a.size ? goal - a.size : a.mdd;
  const reach = Math.min(1, up ? (a.balance - a.size) / gainSpan : (a.size - a.balance) / lossSpan);
  const left = up ? START_AT : START_AT * (1 - reach), width = up ? (100 - START_AT) * reach : START_AT * reach;
  const tone = up ? WIN : LOSS;
  return (
    <div style={{ position: 'relative', height, background: FIELD, boxShadow: `0 0 0 1px ${INK}` }}>
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${left}%`, width: `${width}%`, background: tone, boxShadow: `0 0 6px ${tone}` }} />
      <div style={{ position: 'absolute', top: -2, bottom: -2, left: `calc(${START_AT}% - 1px)`, width: 2, background: TEAL }} />
    </div>
  );
}

/** The firm's account number, short: its letter prefix and last four digits (LDE1006721605001 → LDE…5001) */
export const shortId = (id: string | null) => {
  if (!id) return null;
  const clean = id.replace(/\s/g, ''), prefix = clean.match(/^[A-Za-z]+/)?.[0]?.toUpperCase() ?? '';
  return clean.length <= prefix.length + 4 ? clean.toUpperCase() : `${prefix}…${clean.slice(-4)}`;
};

/* Hovering an account shows where it stands: number, room, payouts, cooldown, consistency.
   Fixed to the screen so no panel edge clips it. */
function AccountHover({ a, children }: { a: MarathonAccount; children: ReactNode }) {
  const [at, setAt] = useState<{ x: number; y: number; below: boolean } | null>(null);
  const show = (e: React.MouseEvent<HTMLSpanElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setAt({ x: Math.min(window.innerWidth - 268, Math.max(8, r.left + r.width / 2 - 130)), y: r.top > 360 ? r.top - 8 : r.bottom + 8, below: r.top <= 360 });
  };
  const c = a.consistency;
  const days = (iso: string) => Math.max(1, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));
  const cell = (name: string, value: ReactNode, tone?: string, sub?: ReactNode) => (
    <div>
      <div style={{ ...label, fontSize: 8, marginBottom: 3 }}>{name}</div>
      <div style={{ fontFamily: NUM_FONT, fontSize: 15, color: tone ?? ICE, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ ...label, fontSize: 8, textTransform: 'none', letterSpacing: '0.03em', marginTop: 2 }}>{sub}</div>}
    </div>
  );
  // The bar runs from the loss line to the goal: the eval's target, or a funded account's payout line
  const goal = a.status === 'eval' ? a.target : a.payoutLine;
  const span = goal != null ? goal - a.floor : 0;
  const pos = (v: number) => `${Math.max(0, Math.min(1, (v - a.floor) / span)) * 100}%`;
  const over = c?.rule != null && c.pct != null && c.pct > c.rule;
  return (
    <span style={{ display: 'inline-flex' }} onMouseEnter={show} onMouseLeave={() => setAt(null)} onFocus={show as never} onBlur={() => setAt(null)}>
      {children}
      {at && createPortal(
        <div role="tooltip" style={{ position: 'fixed', left: at.x, top: at.y, transform: at.below ? undefined : 'translateY(-100%)', width: 260, zIndex: 60, pointerEvents: 'none',
          background: PANEL, boxShadow: `0 0 0 2px ${RIM}, 0 0 0 4px ${INK}, 0 10px 24px rgba(0,0,0,0.5)`, padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: TITLE_FONT, fontSize: 16, color: a.role === 'master' ? GOLD : ICE }}>{a.label}</span>
            <span style={{ ...label, fontSize: 8 }}>{a.firm} · {k(a.size)}</span>
          </div>
          {a.platform_id && <div style={{ fontFamily: NUM_FONT, fontSize: 11, color: TEAL, wordBreak: 'break-all', marginTop: -2 }}>{a.platform_id}</div>}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ ...label, fontSize: 8, padding: '2px 6px', boxShadow: `0 0 0 1px ${RIM}` }}>{a.trail === 'eod' ? 'EOD drawdown' : 'Intraday drawdown'}</span>
            <span style={{ ...label, fontSize: 8, padding: '2px 6px', boxShadow: `0 0 0 1px ${RIM}`, color: a.status === 'funded' ? WIN : a.status === 'eval' ? GOLD : LOSS }}>{a.status === 'eval' ? 'Eval' : a.status}</span>
            {a.locked && <span style={{ ...label, fontSize: 8, padding: '2px 6px', boxShadow: `0 0 0 1px ${RIM}` }}>Floor locked</span>}
            {a.iced && a.iced_until && <span style={{ ...label, fontSize: 8, padding: '2px 6px', boxShadow: `0 0 0 1px ${RIM}`, color: 'var(--pal-frost)' }}>Cooldown · {days(a.iced_until)}d</span>}
          </div>
          <div style={{ height: 1, background: RIM, margin: '2px 0' }} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px' }}>
            {cell('Balance', usd(a.balance))}
            {a.status === 'eval' && a.target != null
              ? cell('Profit target', <>{usd(Math.max(0, a.balance - a.size))}<span style={{ color: TEAL, fontSize: 12 }}> / {usd(a.target - a.size)}</span></>, a.balance > a.size ? WIN : undefined)
              : cell('Payouts', `${a.payouts_taken} taken`, undefined, `next ${usd(a.nextPayout)}`)}
            {cell('Trading days', c?.days ?? 0, (c?.days ?? 0) > 0 ? WIN : undefined)}
            {cell('Daily loss limit', a.dll != null ? usd(a.dll) : '—')}
            <div style={{ gridColumn: '1 / -1' }}>
              {cell(`Consistency${c?.rule != null ? ` (${Math.round(c.rule * 100)}%)` : ''}`,
                c?.pct != null ? `${(c.pct * 100).toFixed(2)}%` : '—',
                c?.pct == null ? undefined : over ? LOSS : WIN,
                c?.pct == null ? 'Shows once there is profit and a best day'
                  : c.left != null && c.left > 0 ? `${usd(c.left)} left` : c.rule != null ? 'Within the rule' : `Best day ${usd(c.bestDay)}`)}
            </div>
          </div>
          {goal != null && span > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ position: 'relative', height: 16 }}>
                {a.size > a.floor && a.size < goal && <>
                  <span style={{ ...label, fontSize: 7, position: 'absolute', left: pos(a.size), transform: 'translateX(-50%)', top: -2 }}>Start</span>
                </>}
              </div>
              <div style={{ position: 'relative', height: 4, background: FIELD }}>
                {a.size > a.floor && a.size < goal && <span style={{ position: 'absolute', left: pos(a.size), top: -4, width: 2, height: 12, background: TEAL }} />}
                <span style={{ position: 'absolute', top: 0, height: '100%',
                  left: a.balance >= a.size ? pos(a.size) : pos(a.balance), width: `calc(${pos(Math.max(a.balance, a.size))} - ${a.balance >= a.size ? pos(a.size) : pos(a.balance)})`,
                  background: a.balance >= a.size ? WIN : LOSS, boxShadow: `0 0 6px ${a.balance >= a.size ? WIN : LOSS}` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <div><div style={{ fontFamily: NUM_FONT, fontSize: 12, color: LOSS }}>{usd(a.floor)}</div><div style={{ ...label, fontSize: 7, color: LOSS }}>{a.locked ? 'Locked floor' : 'Max loss'}</div></div>
                <div style={{ textAlign: 'right' }}><div style={{ fontFamily: NUM_FONT, fontSize: 12, color: WIN }}>{usd(goal)}</div><div style={{ ...label, fontSize: 7, color: WIN }}>{a.status === 'eval' ? 'Target' : 'Payout line'}</div></div>
              </div>
            </div>
          )}
        </div>,
        document.body,
      )}
    </span>
  );
}

function Node(props: { a: MarathonAccount; state: NodeState; selected: boolean; onSelect: () => void }) {
  return <AccountHover a={props.a}><NodeTile {...props} /></AccountHover>;
}
function NodeTile({ a, state, selected, onSelect }: { a: MarathonAccount; state: NodeState; selected: boolean; onSelect: () => void }) {
  const head = a.role === 'master' ? 'var(--pal-node)' : state === 'on' ? GOLD : 'var(--pal-node-off)';
  const headText = a.role === 'master' ? GOLD : state === 'on' ? INK : 'var(--pal-node-off-text)';
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={state === 'on' ? 'pal-firing' : undefined}
      style={{
        width: 150, padding: 0, border: 'none', cursor: 'pointer', textAlign: 'left', background: PANEL,
        boxShadow: selected ? `0 0 0 2px ${ICE}, 0 0 0 4px ${INK}, 0 0 18px rgba(212,241,247,0.35)` : `0 0 0 2px ${state === 'on' ? ACCENT : RIM}, 0 0 0 4px ${INK}`,
        opacity: state === 'retired' ? 0.45 : state === 'iced' || state === 'off' ? 0.72 : 1,
      }}
    >
      <div style={{ background: head, color: headText, padding: '4px 8px', fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.14em', textTransform: CASE, display: 'flex', justifyContent: 'space-between' }}>
        <span>{a.role === 'master' ? 'Master' : 'Sub'} · {k(a.size)}</span>
        {a.iced && <Snowflake size={10} />}
        {a.locked && !a.iced && <Lock size={10} />}
      </div>
      <div style={{ padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontFamily: TITLE_FONT, fontSize: 18, color: ICE }}>{a.label}</span>
          <span style={{ ...label, fontSize: 8, color: a.status === 'funded' ? WIN : a.status === 'eval' ? GOLD : LOSS }}>
            {a.status === 'eval' ? 'evaluation' : a.status}
          </span>
        </div>
        {shortId(a.platform_id) && <div style={{ fontFamily: NUM_FONT, fontSize: 10, color: TEAL, marginTop: -3, letterSpacing: '0.04em' }}>{shortId(a.platform_id)}</div>}
        <div style={{ fontFamily: NUM_FONT, fontSize: 20, color: ICE, lineHeight: 1 }}>{usd(a.balance)}</div>
        <StandBar a={a} />
        {a.targetHit && <span style={{ ...label, fontSize: 8, color: GOLD }}>Target hit</span>}
        {a.payoutReady && <span style={{ ...label, fontSize: 8, color: WIN }}>Payout ready</span>}
      </div>
    </button>
  );
}

function MiniNode(props: { a: MarathonAccount; state: NodeState; selected: boolean; onSelect: () => void }) {
  return <AccountHover a={props.a}><MiniTile {...props} /></AccountHover>;
}
function MiniTile({ a, state, selected, onSelect }: { a: MarathonAccount; state: NodeState; selected: boolean; onSelect: () => void }) {
  const on = state === 'on';
  return (
    <button type="button" onClick={onSelect} aria-pressed={selected}
      className={on ? 'pal-firing' : undefined}
      style={{
        width: 84, padding: '6px 7px', border: 'none', cursor: 'pointer', textAlign: 'left',
        background: a.role === 'master' ? 'var(--pal-node)' : on ? 'var(--pal-tint-gold)' : PANEL,
        boxShadow: selected ? `0 0 0 2px ${ICE}, 0 0 0 4px ${INK}` : `0 0 0 2px ${on ? GOLD : RIM}, 0 0 0 4px ${INK}`,
        opacity: state === 'retired' ? 0.45 : state === 'off' || state === 'iced' ? 0.7 : 1,
        display: 'flex', flexDirection: 'column', gap: 4,
      }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: TITLE_FONT, fontSize: 15, color: a.role === 'master' ? GOLD : ICE }}>{a.label}</span>
        {a.iced ? <Snowflake size={9} color="var(--pal-frost)" /> : <span style={{ width: 6, height: 6, background: a.status === 'funded' ? WIN : a.status === 'eval' ? GOLD : LOSS }} />}
      </span>
      {shortId(a.platform_id) && <span style={{ fontFamily: NUM_FONT, fontSize: 9, color: TEAL, marginTop: -2 }}>{shortId(a.platform_id)}</span>}
      <StandBar a={a} height={4} />
    </button>
  );
}

function Column({ tag, on, children }: { tag: string; on: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <span style={{ ...label, fontSize: 9, padding: '4px 9px', color: on ? ON_ACCENT : TEAL, background: on ? GOLD : FIELD, boxShadow: `0 0 0 2px ${INK}` }}>
        {tag}
      </span>
      {children}
    </div>
  );
}

function Wire({ d, on }: { d: string; on: boolean }) {
  return (
    <>
      <path d={d} fill="none" stroke={RIM} strokeWidth={2} vectorEffect="non-scaling-stroke" />
      {on && <path d={d} fill="none" stroke={ACCENT} strokeWidth={2.5} strokeDasharray="8 4" vectorEffect="non-scaling-stroke" className="pal-wire-on" />}
    </>
  );
}

/* Pair / cluster phase: master on top in every trade, two sub groups that swap turns */
function TreeCohort({ master, left, right, leftOn, rightOn, node }: {
  master: MarathonAccount[]; left: MarathonAccount[]; right: MarathonAccount[]; leftOn: boolean; rightOn: boolean;
  node: (a: MarathonAccount, on: boolean) => ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ display: 'flex', gap: 14 }}>{master.map(m => node(m, leftOn || rightOn))}</div>
      <div style={{ ...label, fontSize: 9, fontStyle: 'italic', margin: '6px 0' }}>in every trade</div>
      {/* connector: stem + crossbar + two drops; the side taking the trade carries live current */}
      <svg viewBox="0 0 360 28" preserveAspectRatio="none" style={{ width: 'min(360px, 70%)', height: 28, overflow: 'visible' }} aria-hidden>
        <Wire d="M180 0 V12 H1 V28" on={leftOn} />
        <Wire d="M180 0 V12 H359 V28" on={rightOn} />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 'clamp(40px, 12vw, 160px)', flexWrap: 'wrap' }}>
        <Column tag={leftOn ? 'On — takes the trade' : 'Off — sits out'} on={leftOn}>{left.map(a => node(a, leftOn))}</Column>
        <Column tag={rightOn ? 'On — takes the trade' : 'Off — sits out'} on={rightOn}>{right.map(a => node(a, rightOn))}</Column>
      </div>
      <div style={{ width: 'min(360px, 70%)', height: 12, borderBottom: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}`, marginTop: 10 }} />
      <div style={{ ...label, fontSize: 9, color: GOLD, marginTop: 6 }}>⇄ they swap every trade — whoever was off comes on next</div>
    </div>
  );
}

/* After the restructure: four sub-cohort crests around the Master Cohort */
function RestructuredMap({ masters, quads, mini, selectedId, onSelect }: {
  masters: MarathonAccount[];
  quads: { cohort: MarathonCohort; accounts: MarathonAccount[]; on: boolean }[];
  mini: (a: MarathonAccount, on: boolean) => ReactNode;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const crest = (c: MarathonCohort) => {
    const q = quads.find(x => x.cohort === c);
    if (!q) return <div style={{ ...label, fontSize: 9, opacity: 0.5, alignSelf: 'center', justifySelf: 'center' }}>{COHORT_NAME[c]} — not bought yet</div>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        <span style={{ ...label, fontSize: 9, color: q.on ? GOLD : TEAL }}>{COHORT_NAME[c]} · M + c{c}</span>
        <CohortEmblem subs={q.accounts.slice(0, 4)} master={q.accounts[4]} on={q.on} selectedId={selectedId} onSelect={onSelect} width={240} />
      </div>
    );
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 18, justifyItems: 'center' }}>
      {crest('A')}{crest('B')}
      <div style={{ gridColumn: '1 / -1', padding: 12, background: 'var(--pal-node)', boxShadow: `0 0 0 2px ${GOLD}, 0 0 0 4px ${INK}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
        <span style={{ ...label, fontSize: 9, color: GOLD }}>Master Cohort · in every trade</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>{masters.map(a => mini(a, true))}</div>
      </div>
      {crest('C')}{crest('D')}
    </div>
  );
}

/** The next free label in a cohort: its master (MA) first, then subs sA1, sA2… */
function nextLabel(accounts: MarathonAccount[], cohort: MarathonCohort, role: 'master' | 'sub') {
  const live = accounts.filter(a => a.status === 'eval' || a.status === 'funded');
  if (role === 'master') return `M${cohort}`;
  const taken = new Set(live.map(a => a.label.toLowerCase()));
  for (let n = 1; ; n++) if (!taken.has(`s${cohort}${n}`.toLowerCase())) return `s${cohort}${n}`;
}

function AddAccount({ firms, accounts, onAdd }: { firms: string[]; accounts: MarathonAccount[]; onAdd: (a: Parameters<typeof marathonApi.addAccount>[1]) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  // Start where the build is: the first cohort still short of five, its master if it has none, otherwise a sub
  const live = accounts.filter(a => a.status === 'eval' || a.status === 'funded');
  const startCohort = (['A', 'B', 'C', 'D'] as MarathonCohort[]).find(c => live.filter(a => a.cohort === c).length < 5) ?? 'A';
  const startRole: 'master' | 'sub' = live.some(a => a.cohort === startCohort && a.role === 'master') ? 'sub' : 'master';
  const [cohort, setCohort] = useState<MarathonCohort>(startCohort);
  const [role, setRole] = useState<'master' | 'sub'>(startRole);
  const [labelText, setLabelText] = useState(() => nextLabel(accounts, startCohort, startRole));
  // Keep the suggested name in step with cohort and role (and with each account added) until it's typed over
  const [typed, setTyped] = useState(false);
  // Once the cohort has its master, the next one in is a sub
  useEffect(() => {
    if (role === 'master' && accounts.some(a => (a.status === 'eval' || a.status === 'funded') && a.cohort === cohort && a.role === 'master')) { setRole('sub'); setSize(50000); }
  }, [accounts]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!typed) setLabelText(nextLabel(accounts, cohort, role)); }, [accounts, cohort, role, typed]);
  const [size, setSize] = useState(startRole === 'master' ? 100000 : 50000);
  const [firm, setFirm] = useState(() => live[live.length - 1]?.firm ?? 'Apex');
  const [fromMoat, setFromMoat] = useState(false);
  // An account bought before the tracker: where it stands today
  const [underway, setUnderway] = useState(false);
  const [stage, setStage] = useState<'eval' | 'funded'>('eval');
  const [balance, setBalance] = useState('');
  const [threshold, setThreshold] = useState('');
  const [payouts, setPayouts] = useState('0');
  const [lastPayout, setLastPayout] = useState('');
  const [paid, setPaid] = useState('');
  const [number, setNumber] = useState('');
  const [dll, setDll] = useState('');
  const [trail, setTrail] = useState<'eod' | 'intraday' | ''>('');  // '' follows the firm
  const [daysTraded, setDaysTraded] = useState('');
  const [bestDay, setBestDay] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const num = (v: string) => (v.trim() === '' ? undefined : Number(v.replace(/[$,\s]/g, '')));
      await onAdd({
        label: labelText, cohort, role, size, firm, paid_from_moat: underway ? false : fromMoat, platform_id: number.trim() || undefined, dll: num(dll), trail: trail || undefined,
        existing: underway ? {
          status: stage, balance: num(balance) ?? 0, threshold: num(threshold),
          payouts_taken: stage === 'funded' ? num(payouts) ?? 0 : 0,
          last_payout_on: stage === 'funded' && Number(payouts) > 0 && lastPayout ? lastPayout : undefined,
          cost_paid: num(paid), days_traded: num(daysTraded), best_day: num(bestDay),
        } : undefined,
      });
      setTyped(false); setNumber(''); setDll(''); setDaysTraded(''); setBestDay(''); setBalance(''); setThreshold(''); setPayouts('0'); setLastPayout(''); setPaid('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add account');
    }
  };

  if (!open) return <Btn onClick={() => setOpen(true)}><Plus size={10} style={{ verticalAlign: -1 }} /> Add account</Btn>;
  const sel = (value: string, set: (v: string) => void, opts: [string, string][]) => (
    <select style={input} value={value} onChange={e => set(e.target.value)}>{opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select>
  );
  return (
    <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, alignItems: 'end' }}>
      <label><div style={{ ...label, marginBottom: 5 }}>Label</div><input style={input} placeholder="sA3" value={labelText} onChange={e => { setTyped(true); setLabelText(e.target.value); }} required /></label>
      <label><div style={{ ...label, marginBottom: 5 }}>Cohort</div>{sel(cohort, v => setCohort(v as MarathonCohort), (Object.keys(COHORT_NAME) as MarathonCohort[]).map(c => [c, COHORT_NAME[c]]))}</label>
      <label><div style={{ ...label, marginBottom: 5 }}>Role</div>{sel(role, v => setRole(v as 'master' | 'sub'), [['master', 'Master'], ['sub', 'Sub']])}</label>
      <label><div style={{ ...label, marginBottom: 5 }}>Size</div>{sel(String(size), v => setSize(Number(v)), [['50000', '50k'], ['100000', '100k']])}</label>
      <label><div style={{ ...label, marginBottom: 5 }}>Firm</div>{sel(firm, setFirm, firms.map(f => [f, f]))}</label>
      <label title="End of day: the max loss moves up only when a day closes higher. Intraday: it follows every new high.">
        <div style={{ ...label, marginBottom: 5 }}>Drawdown</div>
        {sel(trail || (firm === 'Lucid' ? 'eod' : 'intraday'), v => setTrail(v as 'eod' | 'intraday'), [['eod', 'End of day'], ['intraday', 'Intraday']])}
      </label>
      <label title="The firm's daily loss limit on this account, if it has one">
        <div style={{ ...label, marginBottom: 5 }}>Daily loss limit</div><input style={input} inputMode="decimal" placeholder="none" value={dll} onChange={e => setDll(e.target.value)} />
      </label>
      <label title="The account number the firm gave it, e.g. LDE1006721605001">
        <div style={{ ...label, marginBottom: 5 }}>Account #</div><input style={input} placeholder="LDE1006721605001" value={number} onChange={e => setNumber(e.target.value)} />
      </label>
      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ ...label, marginRight: 4 }}>Starting</span>
        {([[false, 'Fresh'], [true, 'Already under way']] as const).map(([v, t]) => (
          <button key={t} type="button" onClick={() => setUnderway(v)} aria-pressed={underway === v} style={{
            fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.12em', textTransform: CASE, padding: '5px 9px', border: 'none', cursor: 'pointer',
            background: underway === v ? GOLD : FIELD, color: underway === v ? ON_ACCENT : TEAL, boxShadow: `0 0 0 2px ${INK}` }}>{t}</button>
        ))}
        <span style={{ ...label, fontSize: 8, opacity: 0.7, textTransform: 'none', letterSpacing: '0.04em' }}>
          {underway ? 'Bought before the tracker: enter where it stands today' : 'A new eval, starting at its full size'}
        </span>
      </div>
      {underway ? (<>
        <label><div style={{ ...label, marginBottom: 5 }}>Stage</div>{sel(stage, v => setStage(v as 'eval' | 'funded'), [['eval', 'Eval'], ['funded', 'Funded']])}</label>
        <label><div style={{ ...label, marginBottom: 5 }}>Balance now</div><input style={input} inputMode="decimal" placeholder={String(size + 1240)} value={balance} onChange={e => setBalance(e.target.value)} required /></label>
        <label title="The liquidation / drawdown level your firm's dashboard shows today">
          <div style={{ ...label, marginBottom: 5 }}>Drawdown threshold</div><input style={input} inputMode="decimal" placeholder={String(size - (size === 50000 ? 2000 : 3000) + 600)} value={threshold} onChange={e => setThreshold(e.target.value)} />
        </label>
        {stage === 'funded' && <label><div style={{ ...label, marginBottom: 5 }}>Payouts taken</div><input style={input} inputMode="numeric" value={payouts} onChange={e => setPayouts(e.target.value)} /></label>}
        {stage === 'funded' && Number(payouts) > 0 && <label><div style={{ ...label, marginBottom: 5 }}>Last payout</div><input style={input} type="date" value={lastPayout} onChange={e => setLastPayout(e.target.value)} /></label>}
        <label><div style={{ ...label, marginBottom: 5 }}>Days traded</div><input style={input} inputMode="numeric" placeholder="3" value={daysTraded} onChange={e => setDaysTraded(e.target.value)} /></label>
        <label title="Your biggest single winning day on this account so far"><div style={{ ...label, marginBottom: 5 }}>Best day</div><input style={input} inputMode="decimal" placeholder="758" value={bestDay} onChange={e => setBestDay(e.target.value)} /></label>
        <label><div style={{ ...label, marginBottom: 5 }}>Paid for it</div><input style={input} inputMode="decimal" placeholder={`$${size === 50000 ? 109 : 159}`} value={paid} onChange={e => setPaid(e.target.value)} /></label>
      </>) : (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8 }}>
          <input type="checkbox" checked={fromMoat} onChange={e => setFromMoat(e.target.checked)} style={{ accentColor: ACCENT }} />
          <span style={{ ...label, color: ICE }}>Paid from MOAT</span>
        </label>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={{ fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.1em', textTransform: CASE, padding: '6px 9px', border: 'none', cursor: 'pointer', background: TEAL, color: INK, boxShadow: `0 0 0 2px ${INK}` }}>Add</button>
        <Btn onClick={() => setOpen(false)}>Cancel</Btn>
      </div>
      {error && <div style={{ ...label, color: LOSS, gridColumn: '1 / -1' }}>{error}</div>}
    </form>
  );
}

function Stat2({ k: name, v, tone }: { k: string; v: string; tone?: string }) {
  return (
    <div style={{ background: FIELD, padding: '8px 10px', boxShadow: `0 0 0 2px ${RIM}` }}>
      <div style={{ ...label, fontSize: 9 }}>{name}</div>
      <div style={{ fontFamily: NUM_FONT, fontSize: 22, color: tone ?? ICE, lineHeight: 1.1 }}>{v}</div>
    </div>
  );
}

/* Every detail of an account, editable: fixes entries made by mistake and keeps an under-way account in step with the firm */
function EditAccount({ a, onSave, onCancel }: { a: MarathonAccount; onSave: (changes: Record<string, string | number | null>) => void; onCancel: () => void }) {
  const [f, setF] = useState({
    label: a.label, cohort: a.cohort, role: a.role, size: String(a.size), firm: a.firm, status: a.status,
    balance: String(a.balance), threshold: String(a.floor), payouts_taken: String(a.payouts_taken),
    platform_id: a.platform_id ?? '', dll: a.dll != null ? String(a.dll) : '', trail: a.trail,
    days_traded: String(a.prior_days ?? 0), best_day: a.prior_best != null ? String(a.prior_best) : '',
  });
  const up = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const field = (name: string, k: keyof typeof f, extra: { inputMode?: 'decimal' | 'numeric' } = {}) => (
    <label><div style={{ ...label, marginBottom: 5 }}>{name}</div><input style={input} value={f[k]} onChange={up(k)} {...extra} /></label>
  );
  const pick = (name: string, k: keyof typeof f, opts: [string, string][]) => (
    <label><div style={{ ...label, marginBottom: 5 }}>{name}</div>
      <select style={input} value={f[k]} onChange={up(k)}>{opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></label>
  );
  const save = () => {
    const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/[$,\s]/g, '')));
    const out: Record<string, string | number | null> = {
      label: f.label, cohort: f.cohort, role: f.role, size: Number(f.size), firm: f.firm, status: f.status,
      balance: num(f.balance), payouts_taken: num(f.payouts_taken) ?? 0, platform_id: f.platform_id, dll: num(f.dll), trail: f.trail,
      days_traded: num(f.days_traded) ?? 0, best_day: num(f.best_day),
    };
    // Only send the threshold when it was changed, so the floor isn't rebuilt from a rounded copy
    if (Number(f.threshold) !== a.floor) out.threshold = num(f.threshold);
    onSave(out);
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, alignItems: 'end', padding: 12, background: FIELD, boxShadow: `0 0 0 2px ${INK}` }}>
      {field('Label', 'label')}
      {pick('Cohort', 'cohort', (Object.keys(COHORT_NAME) as MarathonCohort[]).map(c => [c, COHORT_NAME[c]]))}
      {pick('Role', 'role', [['master', 'Master'], ['sub', 'Sub']])}
      {pick('Size', 'size', [['50000', '50k'], ['100000', '100k']])}
      {pick('Firm', 'firm', ['Apex', 'Lucid', 'Other'].map(x => [x, x]))}
      {pick('Stage', 'status', [['eval', 'Eval'], ['funded', 'Funded'], ['breached', 'Breached'], ['closed', 'Closed']])}
      {field('Balance', 'balance', { inputMode: 'decimal' })}
      {field('Drawdown threshold', 'threshold', { inputMode: 'decimal' })}
      {pick('Drawdown', 'trail', [['eod', 'End of day'], ['intraday', 'Intraday']])}
      {field('Daily loss limit', 'dll', { inputMode: 'decimal' })}
      {field('Payouts taken', 'payouts_taken', { inputMode: 'numeric' })}
      {field('Days traded before', 'days_traded', { inputMode: 'numeric' })}
      {field('Best day before', 'best_day', { inputMode: 'decimal' })}
      {field('Account #', 'platform_id')}
      <div style={{ display: 'flex', gap: 8, gridColumn: '1 / -1' }}>
        <Btn onClick={save} tone={TEAL} solid>Save changes</Btn>
        <Btn onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
}

/* The firm's account number, editable in place */
function AccountNumber({ username, a, onSaved }: { username: string; a: MarathonAccount; onSaved: (n: string | null) => void }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(a.platform_id ?? '');
  const save = async () => {
    const n = v.trim() || null;
    await marathonApi.setNumber(username, a.id, n).catch(() => undefined);
    onSaved(n); setEdit(false);
  };
  if (!edit) return (
    <button type="button" onClick={() => setEdit(true)} style={{ display: 'block', marginTop: 4, padding: 0, background: 'none', border: 'none', cursor: 'pointer', fontFamily: NUM_FONT, fontSize: 12, color: TEAL }}>
      {a.platform_id ?? '+ Add account number'}
    </button>
  );
  return (
    <form onSubmit={e => { e.preventDefault(); save(); }} style={{ display: 'flex', gap: 6, marginTop: 6 }}>
      <input autoFocus style={{ ...input, width: 220 }} placeholder="LDE1006721605001" value={v} onChange={e => setV(e.target.value)} />
      <button type="submit" style={{ fontFamily: PX_FONT, fontSize: 9, letterSpacing: '0.1em', textTransform: CASE, padding: '5px 9px', border: 'none', cursor: 'pointer', background: TEAL, color: INK }}>Save</button>
    </form>
  );
}

/* Everything about one account, opened from the rotation map */
function AccountDialog({ username, id, onClose, onAction }: {
  username: string; id: string; onClose: () => void;
  onAction: (kind: 'funded' | 'payout' | 'thaw' | 'breach' | 'edit' | 'remove', a: MarathonAccount, changes?: Record<string, string | number | null>) => void;
}) {
  const [d, setD] = useState<MarathonAccountDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  useEffect(() => {
    marathonApi.account(username, id).then(setD).catch(e => setErr(e instanceof Error ? e.message : 'Could not load account'));
  }, [username, id]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const a = d?.account;
  const pct = a ? Math.max(0, Math.min(1, a.room / a.mdd)) : 0;
  const evalPct = a && a.target ? Math.max(0, Math.min(1, (a.balance - a.size) / (a.target - a.size))) : 0;
  return (
    <div role="dialog" aria-label="Account details" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'var(--pal-overlay)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', background: PANEL, padding: 20, boxShadow: pixelFrame(true) }}>
        {err && <div style={{ ...label, color: LOSS }}>{err}</div>}
        {!a && !err && <div style={label}>Loading…</div>}
        {a && d && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
              <div>
                <span style={{ fontFamily: TITLE_FONT, fontSize: 28, color: a.role === 'master' ? GOLD : ICE, textShadow: HEAD_SHADOW }}>{a.label}</span>
                <span style={{ ...label, marginLeft: 10 }}>{COHORT_NAME[a.cohort]} · {a.role} · {k(a.size)} · {a.firm}</span>
                <AccountNumber username={username} a={a} onSaved={n => setD({ ...d, account: { ...a, platform_id: n } })} />
              </div>
              <button type="button" onClick={onClose} aria-label="Close" style={{ ...label, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ ...label, color: a.status === 'funded' ? WIN : a.status === 'eval' ? GOLD : LOSS }}>{a.status === 'eval' ? 'Evaluation' : a.status}</span>
              {a.locked && <span style={{ ...label, color: ICE }}><Lock size={10} style={{ verticalAlign: -1 }} /> Floor locked</span>}
              {a.iced && a.iced_until && <span style={{ ...label, color: 'var(--pal-frost)' }}><Snowflake size={10} style={{ verticalAlign: -1 }} /> On ice until {new Date(a.iced_until).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' })}</span>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <Stat2 k="Balance" v={usd(a.balance)} />
              <Stat2 k="Peak" v={usd(a.peak)} />
              <Stat2 k="Floor" v={usd(a.floor)} tone={a.locked ? WIN : undefined} />
            </div>

            <div>
              <div style={{ ...label, marginBottom: 6 }}>Drawdown room · {usd(a.room)} of {usd(a.mdd)}</div>
              <div style={{ height: 10, background: FIELD, boxShadow: `0 0 0 1px ${INK}` }}>
                <div style={{ width: `${pct * 100}%`, height: '100%', background: pct > 0.5 ? WIN : pct > 0.25 ? GOLD : LOSS }} />
              </div>
            </div>
            {a.status === 'eval' && a.target && (
              <div>
                <div style={{ ...label, marginBottom: 6 }}>Toward target · {usd(a.balance - a.size)} of {usd(a.target - a.size)}</div>
                <div style={{ height: 10, background: FIELD, boxShadow: `0 0 0 1px ${INK}` }}>
                  <div style={{ width: `${evalPct * 100}%`, height: '100%', background: GOLD }} />
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              <Stat2 k="Trades" v={String(d.stats.trades)} />
              <Stat2 k="Win rate" v={d.stats.trades ? `${Math.round(d.stats.winRate * 100)}%` : '—'} />
              <Stat2 k="Net P&L" v={usd(d.stats.net)} tone={d.stats.net >= 0 ? WIN : LOSS} />
              <Stat2 k="Days" v={String(d.stats.tradingDays)} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <Stat2 k="Payouts" v={String(a.payouts_taken)} />
              <Stat2 k="Paid out" v={usd(d.stats.totalPaidOut)} tone={GOLD} />
              <Stat2 k="Next payout" v={a.status === 'funded' ? usd(a.nextPayout) : '—'} tone={a.payoutReady ? WIN : undefined} />
            </div>
            {a.status === 'funded' && !a.payoutReady && (
              <div style={{ ...label, fontSize: 9, lineHeight: 1.6 }}>
                {a.iced ? 'Frozen after its last payout.' : !a.locked ? 'Payouts open once the floor locks.' : 'Not enough cushion above the floor for the next payout yet.'}
              </div>
            )}

            {d.recentFills.length > 0 && (
              <div>
                <div style={{ ...label, marginBottom: 6 }}>Last trades</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {d.recentFills.slice().reverse().map((f, i) => (
                    <span key={i} title={`${f.traded_on} · ${usd(Number(f.pnl))}`}
                      style={{ fontFamily: PX_FONT, fontSize: 10, padding: '4px 6px', color: INK, background: Number(f.pnl) > 0 ? WIN : LOSS }}>
                      {Number(f.pnl) > 0 ? '+' : ''}{usd(Number(f.pnl))}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {editing && <EditAccount a={a} onCancel={() => setEditing(false)} onSave={changes => { onAction('edit', a, changes); onClose(); }} />}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {!editing && <Btn onClick={() => setEditing(true)}>Edit details</Btn>}
              {!removing
                ? <Btn onClick={() => setRemoving(true)} tone={LOSS}>Remove account</Btn>
                : <>
                    <span style={{ ...label, color: LOSS, textTransform: 'none', letterSpacing: '0.03em' }}>Remove {a.label} and its trade history from the tracker?</span>
                    <Btn onClick={() => { onAction('remove', a); onClose(); }} tone={LOSS} solid>Yes, remove</Btn>
                    <Btn onClick={() => setRemoving(false)}>Keep it</Btn>
                  </>}
            </div>
            {(a.status === 'eval' || a.status === 'funded') && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
                {a.status === 'eval' && <Btn onClick={() => { onAction('funded', a); onClose(); }} tone={GOLD} solid={a.targetHit}>Mark funded</Btn>}
                {a.payoutReady && <Btn onClick={() => { onAction('payout', a); onClose(); }} tone={WIN} solid>Payout {usd(a.nextPayout)}</Btn>}
                {a.iced && <Btn onClick={() => { onAction('thaw', a); onClose(); }}>Thaw</Btn>}
                <Btn onClick={() => { onAction('breach', a); onClose(); }} tone={LOSS}>Breach</Btn>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** The rotation drawn the way the playbook draws it: slot order, then each cohort in its current shape. */
export function RotationMap({ board, showRetired = false, selectedId = null, onSelect = () => {} }: {
  board: Pick<MarathonBoard, 'accounts' | 'rotation'>; showRetired?: boolean; selectedId?: string | null; onSelect?: (id: string) => void;
}) {
  const next = board.rotation.next;
  const liveAccounts = board.accounts.filter(a => a.status === 'eval' || a.status === 'funded');
  const shown = showRetired ? board.accounts : liveAccounts;
  const cohorts = (Object.keys(COHORT_NAME) as MarathonCohort[]).filter(c => shown.some(a => a.cohort === c));
  const slotOf = (ids: string[]) => board.rotation.slots.findIndex(sl => ids.length > 0 && ids.every(id => sl.accountIds.includes(id)));
  const isNext = (i: number) => i >= 0 && i === next?.index;
  const node = (a: MarathonAccount, on: boolean) => (
    <Node key={a.id} a={a} selected={a.id === selectedId} onSelect={() => onSelect(a.id)}
      state={a.status === 'breached' || a.status === 'closed' ? 'retired' : a.iced ? 'iced' : on ? 'on' : 'off'} />
  );
  const mini = (a: MarathonAccount, on: boolean) => (
    <MiniNode key={a.id} a={a} selected={a.id === selectedId} onSelect={() => onSelect(a.id)}
      state={a.status === 'breached' || a.status === 'closed' ? 'retired' : a.iced ? 'iced' : on ? 'on' : 'off'} />
  );
  const subNo = (a: MarathonAccount) => Number(a.label.match(/(\d+)$/)?.[1] ?? 0);
  const restructured = liveAccounts.some(a => a.cohort === 'M');
  const mapGroups: { key: string; title: string; body: ReactNode; compact?: boolean }[] = restructured ? [{
    key: 'restructured',
    title: 'Master Cohort + four sub-cohorts',
    body: (
      <RestructuredMap
        selectedId={selectedId}
        onSelect={onSelect}
        masters={shown.filter(a => a.cohort === 'M')}
        quads={(['A', 'B', 'C', 'D'] as MarathonCohort[]).filter(c => shown.some(a => a.cohort === c)).map(c => {
          const accs = shown.filter(a => a.cohort === c).sort((x, y) => subNo(x) - subNo(y));
          return { cohort: c, accounts: accs, on: isNext(slotOf(accs.filter(a => a.status === 'eval' || a.status === 'funded').map(a => a.id))) };
        })}
        mini={mini}
      />
    ),
  }] : cohorts.map(c => {
    const group = shown.filter(a => a.cohort === c);
    const masters = group.filter(a => a.role === 'master');
    const subs = group.filter(a => a.role === 'sub').sort((x, y) => subNo(x) - subNo(y));
    if (c === 'M') {
      return { key: c, title: 'Master Cohort · fires on every trade', body: <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>{group.map(a => node(a, true))}</div> };
    }
    const liveGroup = group.filter(a => a.status === 'eval' || a.status === 'funded');
    const complete = liveGroup.length >= 5 && liveGroup.every(a => a.status === 'funded');
    if (restructured || complete) {
      const on = isNext(slotOf(liveGroup.map(a => a.id)));
      return { key: c, title: `${COHORT_NAME[c]} · ${restructured ? `fires with the Master Cohort (M + c${c})` : 'complete'}`, body: <CohortEmblem master={masters[0]} subs={subs.slice(0, 4)} on={on} selectedId={selectedId} onSelect={onSelect} />, compact: true };
    }
    const odd = subs.filter(a => subNo(a) % 2 === 1);
    const even = subs.filter(a => subNo(a) % 2 === 0);
    const liveIds = (xs: MarathonAccount[]) => [...masters, ...xs].filter(a => a.status === 'eval' || a.status === 'funded').map(a => a.id);
    return {
      key: c,
      title: `${COHORT_NAME[c]} · ${liveGroup.length <= 3 ? 'the pair' : 'the cluster'}`,
      body: <TreeCohort master={masters} left={odd} right={even} leftOn={isNext(slotOf(liveIds(odd)))} rightOn={isNext(slotOf(liveIds(even)))} node={node} />,
    };
  });


  return (
    <>
        {/* Order of slots, looping */}
        {board.rotation.slots.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 22 }}>
            {board.rotation.slots.map((sl, i) => {
              const cur = i === next?.index;
              return (
                <span key={sl.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...label, fontSize: 9, padding: '5px 9px', color: cur ? INK : ICE, background: cur ? ACCENT : FIELD, boxShadow: `0 0 0 2px ${cur ? INK : RIM}` }}>
                    {i + 1} · {sl.label}{cur ? ' · next' : ''}
                  </span>
                  {i < board.rotation.slots.length - 1 && <span style={{ color: RIM }}>→</span>}
                </span>
              );
            })}
            <span style={{ ...label, color: GOLD }}>↺</span>
          </div>
        )}

        {/* Completed cohorts sit side by side; trees still building take the full width */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 34, justifyContent: 'center' }}>
          {mapGroups.map(g => (
            <div key={g.key} style={{ flex: g.compact ? '1 1 380px' : '1 1 100%', display: 'flex', flexDirection: 'column', alignItems: g.compact ? 'center' : 'stretch' }}>
              <div style={{ ...label, color: GOLD, marginBottom: 12, alignSelf: 'flex-start' }}>{g.title}</div>
              {g.body}
            </div>
          ))}
          {!mapGroups.length && <div style={label}>No accounts yet — add MA, sA1 and sA2 to open the pair.</div>}
        </div>

    </>
  );
}

export function MarathonView({ username, strategies }: { username: string; strategies: string[] }) {
  const [simulating, setSimulating] = useState(false);
  const [board, setBoard] = useState<MarathonBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState('');
  const [flash, setFlash] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);
  const [showMoney, setShowMoney] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBoard(await marathonApi.board(username));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the rotation');
    }
  }, [username]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, done?: string) => {
    try {
      await fn();
      if (done) setFlash(done);
      await load();
    } catch (err) {
      setFlash(err instanceof Error ? err.message : 'Something went wrong');
    }
  };

  if (error) return <Panel><div style={{ ...label, color: LOSS }}>{error}</div></Panel>;
  if (!board) return null;

  const next = board.rotation.next;
  const byId = new Map(board.accounts.map(a => [a.id, a]));
  const liveAccounts = board.accounts.filter(a => a.status === 'eval' || a.status === 'funded');
  const retired = board.accounts.filter(a => a.status === 'breached' || a.status === 'closed');
  const logTrade = async () => {
    const r = Number(result);
    if (result.trim() === '' || !Number.isFinite(r)) { setFlash('Enter the result in R, e.g. 1.2 or -1'); return; }
    await run(async () => {
      const out = await marathonApi.trade(username, r);
      setResult('');
      setFlash(`${out.slot}: ${out.pnlPerAccount >= 0 ? '+' : ''}${usd(out.pnlPerAccount)} on ${out.accounts.join(', ')}${out.breached.length ? ` · breached & replaced: ${out.breached.join(', ')}` : ''}`);
    });
  };

  const onAction = (kind: 'funded' | 'payout' | 'thaw' | 'breach' | 'edit' | 'remove', a: MarathonAccount, changes?: Record<string, string | number | null>) => {
    if (kind === 'edit' && changes) run(() => marathonApi.edit(username, a.id, changes), `${a.label} updated`);
    if (kind === 'remove') run(() => marathonApi.remove(username, a.id), `${a.label} removed`);
    if (kind === 'funded') run(() => marathonApi.markFunded(username, a.id), `${a.label} is funded`);
    if (kind === 'thaw') run(() => marathonApi.thaw(username, a.id), `${a.label} is back in the rotation`);
    if (kind === 'payout') run(() => marathonApi.payout(username, a.id), `${a.label} paid ${usd(a.nextPayout)} — 50% to the MOAT, on ice`);
    if (kind === 'breach' && window.confirm(`Mark ${a.label} as breached? A replacement eval is bought right away (from the MOAT if it can cover it).`)) {
      run(() => marathonApi.breach(username, a.id), `${a.label} breached — replacement eval added`);
    }
  };

  const k1 = board.kpis;
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => setSimulating(true)} title="Walk through the eight build-out steps"
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', border: 'none', cursor: 'pointer', background: ACCENT, color: ON_ACCENT, fontFamily: PX_FONT, fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: CASE, borderRadius: 'var(--pal-radius, 0)', boxShadow: `0 0 18px ${ACCENT}` }}>
          <GraduationCap size={15} /> Tutorial
        </button>
      </div>
      {simulating && <MarathonPhasesPreview onClose={() => setSimulating(false)} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12 }}>
        <Kpi name="Accounts" value={`${k1.accountsOpen}/25`} sub={`${liveAccounts.filter(a => a.status === 'funded').length} funded`} />
        <Kpi name="Funded capital" value={k(k1.fundedCapital)} sub="of $1.5M" />
        <Kpi name="MOAT" value={usd(k1.moat)} tone={k1.moat >= 0 ? GOLD : LOSS} />
        <Kpi name="Breaches" value={String(k1.fundedBreaches)} tone={k1.fundedBreaches ? LOSS : ICE} />
        <Kpi name="Build step" value={`${board.step.n}/8`} sub={board.step.title} />
        <Kpi name="Risk / trade" value={usd(board.sizing.riskPerTrade)} sub={board.sizing.prescription ? `Your ${board.sizing.prescription.tier} prescription` : `${k(board.sizing.smallestDrawdown)} DD ÷ ${board.sizing.streak + 1}`} tone={WIN} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
        <Panel style={{ flex: '2 1 460px' }}>
          <Title>Next trade{next ? ` · ${next.label}` : ''}</Title>
          {next ? (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                {next.firing.map(id => (
                  <span key={id} style={{ ...label, color: ON_ACCENT, background: ACCENT, padding: '5px 9px', boxShadow: `0 0 0 2px ${INK}` }}>{byId.get(id)?.label}</span>
                ))}
                {next.sittingOut.map(id => (
                  <span key={id} style={{ ...label, color: 'var(--pal-frost)', background: FIELD, padding: '5px 9px', boxShadow: `0 0 0 2px ${RIM}`, display: 'flex', gap: 4, alignItems: 'center' }}>
                    <Snowflake size={10} /> {byId.get(id)?.label}
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input style={{ ...input, width: 130 }} inputMode="decimal" placeholder="Result in R" value={result}
                  onChange={e => setResult(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') logTrade(); }} aria-label="Result in R" />
                <Btn onClick={logTrade} solid>Log trade · {usd(board.sizing.riskPerTrade)}/R</Btn>
                <Btn onClick={() => run(() => marathonApi.skip(username), 'Skipped to the next slot')}><SkipForward size={10} style={{ verticalAlign: -1 }} /> Skip</Btn>
              </div>
            </>
          ) : <div style={label}>Add your first accounts to start the rotation.</div>}
          {flash && <div style={{ ...label, color: ICE, marginTop: 12 }}>{flash}</div>}
        </Panel>

        <Panel style={{ flex: '1 1 300px' }}>
          <Title>Step {board.step.n} · {board.step.title}</Title>
          <div style={{ fontFamily: PX_FONT, fontSize: 11, lineHeight: 1.7, color: ICE, marginBottom: 14 }}>{board.step.next}</div>
          <div style={{ ...label, marginBottom: 6 }}>Size off</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {strategies.map(s => (
              <Btn key={s} onClick={() => run(() => marathonApi.setStrategy(username, s))} solid={s === board.strategy}>{s}</Btn>
            ))}
          </div>
          <div style={{ ...label, fontSize: 9, marginTop: 10, lineHeight: 1.6 }}>
            {board.sizing.prescription
              ? `Sized by your ${board.sizing.prescription.tier} ${board.strategy} prescription — change it in the Prescription Engine`
              : board.sizing.ownData
              ? `Worst losing streak in your last ${board.sizing.sampleSize} ${board.strategy} trades: ${board.sizing.streak}, plus one`
              : `Under 20 logged ${board.strategy} trades — using the planned streak of ${board.sizing.streak}, plus one`}
          </div>
        </Panel>
      </div>

      <Panel>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
          <Title>The rotation</Title>
          {retired.length > 0 && <Btn onClick={() => setShowRetired(v => !v)}>{showRetired ? 'Hide' : 'Show'} retired ({retired.length})</Btn>}
        </div>

        <RotationMap board={board} showRetired={showRetired} selectedId={selectedId} onSelect={id => setSelectedId(cur => (cur === id ? null : id))} />

        {selectedId && <AccountDialog username={username} id={selectedId} onClose={() => setSelectedId(null)} onAction={onAction} />}

        <div style={{ marginTop: 20 }}>
          <AddAccount firms={board.firms} accounts={board.accounts} onAdd={async a => { await marathonApi.addAccount(username, a); setFlash(`${a.label} added`); await load(); }} />
        </div>
      </Panel>

      <Panel>
        <button type="button" onClick={() => setShowMoney(v => !v)} style={{ ...label, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {showMoney ? '▾' : '▸'} MOAT & payouts
        </button>
        {showMoney && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 14 }}>
            <div style={{ flex: '1 1 260px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, alignContent: 'start' }}>
              <Kpi name="Tax 20%" value={usd(k1.taxReserve)} />
              <Kpi name="Self-pay 30%" value={usd(k1.selfPay)} />
              <Kpi name="Paid out" value={usd(k1.grossPayouts)} />
            </div>
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ ...label, marginBottom: 8 }}>Recent payouts</div>
              {board.payouts.length ? board.payouts.map(p => (
                <div key={p.id} style={{ ...label, color: ICE, display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${RIM}` }}>
                  <span>{p.label} · {new Date(p.paid_on).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
                  <span style={{ color: WIN }}>{usd(Number(p.amount))}</span>
                </div>
              )) : <div style={label}>None yet</div>}
            </div>
            <div style={{ flex: '1 1 260px' }}>
              <div style={{ ...label, marginBottom: 8 }}>MOAT ledger</div>
              {board.ledger.length ? board.ledger.map(l => (
                <div key={l.id} style={{ ...label, color: ICE, display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: `1px solid ${RIM}` }}>
                  <span>{l.note ?? l.kind}</span>
                  <span style={{ color: Number(l.amount) >= 0 ? GOLD : LOSS }}>{Number(l.amount) >= 0 ? '+' : ''}{usd(Number(l.amount))}</span>
                </div>
              )) : <div style={label}>Empty</div>}
            </div>
          </div>
        )}
      </Panel>
    </>
  );
}
